export interface MenuItem {
    name: string;
    link?: string | undefined | null;
    iconUrl?: string | undefined | null;
    customClassName?: string | undefined | null;
    specialAttributes?:
        | { [key: string]: string | undefined | null }
        | undefined
        | null;
    target?: string | undefined | null;
    iconWidth?: number | undefined | null;
    iconHeight?: number | undefined | null;
    items?: MenuItem[] | undefined | null;
    count: number | undefined | null;
    transparentOnMobile?: boolean;
}

export interface PatchedMenuItem extends MenuItem {
    parent: PatchedMenuItem | undefined;
    next: PatchedMenuItem | undefined;
    prev: PatchedMenuItem | undefined;
    items: PatchedMenuItem[] | undefined;
}

export function getDeepestLevel(item: MenuItem | MenuItem[]): number {
    if (Array.isArray(item)) {
        return item.reduce((prev, curr) => {
            const subLevel = getDeepestLevel(curr);
            if (subLevel > prev) return subLevel;
            return prev;
        }, 0);
    }

    if (item.items == null || item.items.length === 0) return 0;

    return 1 + getDeepestLevel(item.items);
}

export function patchMenuItems(
    menuItems: MenuItem[],
    parent: PatchedMenuItem | undefined = undefined
): PatchedMenuItem[] {
    const menuItemsLength = menuItems.length;
    for (let i = 0; i < menuItemsLength; i++) {
        const menuItem = menuItems[i] as PatchedMenuItem;
        menuItem.parent = parent;
        menuItem.prev =
            i == 0 ? undefined : (menuItems[i - 1] as PatchedMenuItem);
        menuItem.next =
            i == menuItemsLength - 1
                ? undefined
                : (menuItems[i + 1] as PatchedMenuItem);

        if (menuItem.items != null && menuItem.items.length > 0) {
            patchMenuItems(menuItem.items, menuItem);
        }
    }
    return menuItems as PatchedMenuItem[];
}

// Return true if this current item shows subitems inside a dropdown
export function isDropdownContainer(
    item: PatchedMenuItem | undefined,
    mobile: boolean
) {
    if (item == null) return false;
    if (hasLink(item)) {
        if (!mobile || !isMulticolumnDropdownContainer(item.parent))
            return false;
    }
    if (item.items == null || item.items.length == 0) return false;
    return true;
}

export function hasLink(item: PatchedMenuItem | undefined) {
    if (item == null) return false;
    if (item.link != null && item.link !== "") return true;
    return false;
}

// Return true if this current item shows subitems inside a multicolumn dropdown
export function isMulticolumnDropdownContainer(
    item: PatchedMenuItem | undefined
): boolean {
    if (item == null) return false;
    if (item.items == null) return false;
    return (
        getDeepestLevel(item.items) > 0 &&
        !isMulticolumnDropdownContainer(item.parent) &&
        !isMulticolumnDropdownContainer(item.parent?.parent)
    );
}

function getRootItem(menuItem: PatchedMenuItem) {
    while (menuItem.parent != null) {
        menuItem = menuItem.parent;
    }
    while (menuItem.prev != null) {
        menuItem = menuItem.prev;
    }
    return menuItem;
}

export function* iterateAllItemsFlattened(
    menuItem: PatchedMenuItem
): Generator<PatchedMenuItem> {
    let currentRootItem: PatchedMenuItem | undefined = getRootItem(menuItem);
    do {
        yield currentRootItem;
        for (const subItem of iterateAllSubItems(currentRootItem))
            yield subItem;
    } while ((currentRootItem = currentRootItem.next) != null);
}

function* iterateAllSubItems(
    menuItem: PatchedMenuItem
): Generator<PatchedMenuItem> {
    if (menuItem.items == null || menuItem.items.length === 0) return;
    for (const subItem of menuItem.items) {
        yield subItem;
        for (const subItem2 of iterateAllSubItems(subItem)) yield subItem2;
    }
}
